import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProcessedError, ErrorSeverity, ErrorCategory } from '../../types/errors';

export interface RoleAssignmentRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  message: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: number;
  processedAt?: number;
  processedBy?: string;
  notes?: string;
}

export interface ErrorState {
  currentError: ProcessedError | null;
  errorHistory: ProcessedError[];
  showNoRolesModal: boolean;
  roleAssignmentRequests: RoleAssignmentRequest[];
  isLoading: boolean;
  lastErrorTimestamp: number | null;
}

const initialState: ErrorState = {
  currentError: null,
  errorHistory: [],
  showNoRolesModal: false,
  roleAssignmentRequests: [],
  isLoading: false,
  lastErrorTimestamp: null,
};

const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    setCurrentError: (state, action: PayloadAction<ProcessedError | null>) => {
      state.currentError = action.payload;
      if (action.payload) {
        state.lastErrorTimestamp = Date.now();
        state.errorHistory.unshift(action.payload);
        
        // Keep only last 50 errors
        if (state.errorHistory.length > 50) {
          state.errorHistory = state.errorHistory.slice(0, 50);
        }
        
        // Show NO_ROLES modal if applicable
        if (action.payload.code === 'NO_ROLES') {
          state.showNoRolesModal = true;
        }
      }
    },
    
    clearCurrentError: (state) => {
      state.currentError = null;
    },
    
    clearErrorHistory: (state) => {
      state.errorHistory = [];
    },
    
    setShowNoRolesModal: (state, action: PayloadAction<boolean>) => {
      state.showNoRolesModal = action.payload;
    },
    
    addRoleAssignmentRequest: (state, action: PayloadAction<RoleAssignmentRequest>) => {
      state.roleAssignmentRequests.unshift(action.payload);
    },
    
    updateRoleAssignmentRequest: (state, action: PayloadAction<{
      id: string;
      updates: Partial<RoleAssignmentRequest>;
    }>) => {
      const { id, updates } = action.payload;
      const index = state.roleAssignmentRequests.findIndex(req => req.id === id);
      if (index !== -1) {
        state.roleAssignmentRequests[index] = {
          ...state.roleAssignmentRequests[index],
          ...updates,
        };
      }
    },
    
    removeRoleAssignmentRequest: (state, action: PayloadAction<string>) => {
      state.roleAssignmentRequests = state.roleAssignmentRequests.filter(
        req => req.id !== action.payload
      );
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Utility actions
    dismissError: (state, action: PayloadAction<string>) => {
      if (state.currentError?.id === action.payload) {
        state.currentError = null;
      }
      state.errorHistory = state.errorHistory.filter(
        error => error.id !== action.payload
      );
    },
    
    // Bulk actions
    clearErrorsByCategory: (state, action: PayloadAction<ErrorCategory>) => {
      state.errorHistory = state.errorHistory.filter(
        error => error.category !== action.payload
      );
      if (state.currentError?.category === action.payload) {
        state.currentError = null;
      }
    },
    
    clearErrorsBySeverity: (state, action: PayloadAction<ErrorSeverity>) => {
      state.errorHistory = state.errorHistory.filter(
        error => error.severity !== action.payload
      );
      if (state.currentError?.severity === action.payload) {
        state.currentError = null;
      }
    },
  },
});

export const {
  setCurrentError,
  clearCurrentError,
  clearErrorHistory,
  setShowNoRolesModal,
  addRoleAssignmentRequest,
  updateRoleAssignmentRequest,
  removeRoleAssignmentRequest,
  setLoading,
  dismissError,
  clearErrorsByCategory,
  clearErrorsBySeverity,
} = errorSlice.actions;

// Selectors
export const selectCurrentError = (state: { error: ErrorState }) => state.error.currentError;
export const selectErrorHistory = (state: { error: ErrorState }) => state.error.errorHistory;
export const selectShowNoRolesModal = (state: { error: ErrorState }) => state.error.showNoRolesModal;
export const selectRoleAssignmentRequests = (state: { error: ErrorState }) => state.error.roleAssignmentRequests;
export const selectIsLoading = (state: { error: ErrorState }) => state.error.isLoading;
export const selectLastErrorTimestamp = (state: { error: ErrorState }) => state.error.lastErrorTimestamp;

// Selectors for specific error types
export const selectErrorsByCode = (state: { error: ErrorState }, code: string) =>
  state.error.errorHistory.filter(error => error.code === code);

export const selectErrorsByCategory = (state: { error: ErrorState }, category: ErrorCategory) =>
  state.error.errorHistory.filter(error => error.category === category);

export const selectErrorsBySeverity = (state: { error: ErrorState }, severity: ErrorSeverity) =>
  state.error.errorHistory.filter(error => error.severity === severity);

export const selectNoRolesErrors = (state: { error: ErrorState }) =>
  state.error.errorHistory.filter(error => error.code === 'NO_ROLES');

export default errorSlice.reducer; 